// The bell at the top of every page: the notifications of Boards (what happened to the tickets you are part of), with a red number until they are read,
// and the switch for push notifications on this device. New ones arrive at once when an Inbox stream is open, and the page looks again every minute.

import { ticketWindowUrl } from '../shared/ticket-window.js';

// The Desktop app (Electron) has no push service, so there the bell shows real notifications of the computer instead.
const inDesktopApp = () => /KolvantageDesktop\//.test(navigator.userAgent);

// (read here, not imported from alerts.js, which needs a browser page to load)
const desktopAlertsOn = () => {
  try { if (JSON.parse(localStorage.getItem('hub_alerts') || '{}').desktop === false) return false; } catch { /* default: on */ }
  return 'Notification' in window && Notification.permission === 'granted';
};

const urlToBytes = (b64) => {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};

const SVG = (path) => `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${path}</svg>`;
// what kind of thing happened: a small drawn icon on the person's picture, and its color
const KINDS = {
  moved: ['<path d="M5 12h14M13 6l6 6-6 6"/>', '#4f46e5', 'Moves'],
  comment: ['<path d="M21 12a8 8 0 01-11.5 7.2L4 20l1-4.6A8 8 0 1121 12z"/>', '#0e9f6e', 'Comments'],
  mention: ['<circle cx="12" cy="12" r="4"/><path d="M16 8v5a3 3 0 006 0v-1a10 10 0 10-4 8"/>', '#d97706', 'Mentions'],
  assigned: ['<circle cx="9" cy="8" r="3.5"/><path d="M3 20c.6-3.5 3-5 6-5s5.4 1.5 6 5M19 8v6M16 11h6"/>', '#0ea5e9', 'Assigned'],
  github: ['<circle cx="6" cy="6" r="2.5"/><circle cx="6" cy="18" r="2.5"/><circle cx="18" cy="18" r="2.5"/><path d="M6 8.5v7M18 15.5V10a3 3 0 00-3-3h-3"/>', '#24292f', 'GitHub'],
  automation: ['<path d="M13 3L5 14h6l-1 7 8-11h-6z"/>', '#7c3aed', 'Automations'],
  reminder: ['<path d="M6 8a6 6 0 0112 0c0 6 2 7 2 7H4s2-1 2-7M10 20a2 2 0 004 0"/>', '#d97706', 'Reminders'],
  reaction: ['<circle cx="12" cy="12" r="9"/><path d="M8 14s1.5 2 4 2 4-2 4-2M9 9.5h.01M15 9.5h.01"/>', '#db2777', 'Reactions'],
  update: ['<path d="M4 20h4L19 9a2.8 2.8 0 00-4-4L4 16z"/>', '#64748b', 'Changes'],
};
// the chips at the top: what to show (one kind at a time); a kind may group several
const TYPE_CHIPS = [['moved', 'Moves', ['moved']], ['comment', 'Comments', ['comment', 'mention']], ['assigned', 'Assigned to me', ['assigned']], ['github', 'GitHub', ['github']], ['automation', 'Automations', ['automation', 'reminder']]];

/** The kind of a notification, from what the hub said (meta) and, for the older ones that have none, from its words. */
export function kindOf(n) {
  const kinds = n.meta?.kinds || [];
  if (n.reason === 'tagged' || n.meta?.mention) return 'mention';
  if (['automation', 'reminder', 'reaction'].includes(n.reason)) return n.reason;
  for (const k of ['comment', 'moved', 'assigned', 'github']) if (kinds.includes(k)) return k;
  if (kinds.length) return 'update';
  const t = n.summary || '';
  if (/ (commented|mentioned you)/.test(t)) return 'comment';
  if (/ moved it from /.test(t)) return 'moved';
  if (/ assigned it to | took you off it/.test(t)) return 'assigned';
  if (/\(GitHub\)|pull request/.test(t)) return 'github';
  return 'update';
}

/** The person (or the automation) the sentence starts with: what the hub knows, else the words before the verb (a person who has left the company). */
export function whoOf(n) {
  const text = n.summary || '';
  if (n.actorName && text.startsWith(n.actorName)) return n.actorName;
  const m = text.match(/^(.{1,60}?) (moved it from |commented|mentioned you|assigned it|took you|took .+ off|changed |renamed it|created it|restored |brought it|reacted |a pull request|changes were)/);
  return m ? m[1] : '';
}

/** "DEV-2889 · Remove Love Horoscope" → { key, name }. */
export const splitTitle = (title) => {
  const at = String(title || '').indexOf(' · ');
  return at < 0 ? { key: '', name: title || '' } : { key: title.slice(0, at), name: title.slice(at + 3) };
};

export function createBell({ api, el, avatarEl, toast }) {
  let items = [];
  let unread = 0;
  let filter = 'unread'; // unread | all | mentions | read
  let typeFilter = ''; // '' or one of TYPE_CHIPS
  let boardFilter = ''; // '' or a board's name
  let query = ''; // the words searched for (in the title and in what happened)
  const open = new Set(); // the tickets whose folded updates are open
  const search = el('input', { type: 'search', class: 'bell-search', placeholder: 'Search notifications…', 'aria-label': 'Search notifications', autocomplete: 'off' });
  let searchTimer = null;
  search.addEventListener('input', () => { clearTimeout(searchTimer); searchTimer = setTimeout(() => { query = search.value.trim(); refresh(); }, 250); });
  const badge = el('span', { class: 'bell-badge', hidden: true });
  const button = el('button', { type: 'button', class: 'bell-btn', 'aria-label': 'Notifications', 'aria-haspopup': 'true', 'aria-expanded': 'false' }, el('span', { 'aria-hidden': 'true' }, '🔔'), badge);
  const panel = el('div', { class: 'bell-panel', hidden: true, role: 'dialog', 'aria-label': 'Notifications' });
  const wrap = el('div', { class: 'bell', hidden: true }, button, panel);

  const ago = (ts) => {
    const s = Math.max(1, Math.round((Date.now() - ts) / 1000));
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.round(s / 60)} min ago`;
    if (s < 86400) return `${Math.round(s / 3600)} h ago`;
    return new Date(ts).toLocaleString(undefined, { weekday: 'short', hour: '2-digit', minute: '2-digit' });
  };
  const dayKey = (ts) => new Date(ts).toDateString();
  const dayLabel = (ts) => {
    const d = new Date(ts);
    const now = new Date();
    if (dayKey(ts) === now.toDateString()) return 'Today';
    const y = new Date(now);
    y.setDate(now.getDate() - 1);
    if (dayKey(ts) === y.toDateString()) return 'Yesterday';
    return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
  };

  const paintBadge = () => {
    badge.hidden = unread === 0;
    badge.textContent = unread > 99 ? '99+' : String(unread);
    button.setAttribute('aria-label', unread ? `Notifications, ${unread} not read` : 'Notifications');
  };

  const read = async (body) => {
    try { unread = (await api('/board-notifications/read', { method: 'POST', body })).unread; } catch { /* the next look */ }
    paintBadge();
  };

  /** What happened, as a sentence: the person in bold, a move as two colored chips, a long message as a quote of up to three lines. */
  function whatNode(n, kind, full = false) {
    const out = el('div', { class: 'bell-what' });
    const box = el('div', {});
    const text = n.summary || '';
    const parts = text.split('; ');
    const actor = whoOf(n);
    parts.forEach((part, i) => {
      const line = el('div', { class: 'bell-line' });
      let rest = part;
      if (i === 0 && actor) { line.append(el('b', {}, actor), ' '); rest = part.slice(actor.length).trim(); }
      // the columns' names come with the notification (a name can hold " to ": "Archive (Uploaded to website)"): the sentence is split only when they are not there (older ones)
      const named = n.meta?.move?.from && n.meta?.move?.to && rest === `moved it from ${n.meta.move.from} to ${n.meta.move.to}` ? ['', n.meta.move.from, n.meta.move.to] : null;
      const move = named || rest.match(/^moved it from (.+?) to (.+)$/);
      const comment = rest.match(/^([^:“]*?):\s*[“"]([\s\S]*)[”"]$/);
      if (move) {
        const m = n.meta?.move;
        const chip = (label, tone) => el('span', { class: `bell-col${tone ? ` tone-${tone}` : ''}` }, label);
        line.append('moved it ', chip(move[1], m?.fromTone), el('span', { class: 'bell-arrow' }, '→'), chip(move[2], m?.toTone));
      } else if (comment) {
        line.append(`${comment[1].trim()}`);
        box.append(line);
        box.append(...quoteNodes(comment[2], full));
        return;
      } else if (!actor && i === 0 && (kind === 'automation' || kind === 'reminder') && (rest.length > 90 || rest.includes('\n'))) {
        box.append(...quoteNodes(rest, full));
        return;
      } else line.append(rest);
      box.append(line);
    });
    out.append(box);
    return out;
  }

  /** A message as a quote: three lines with Show all when it is long (the folded updates show it in full at once). */
  function quoteNodes(text, full) {
    const long = text.length > 150 || text.split('\n').length > 3;
    const quote = el('div', { class: `bell-quote${full || !long ? ' full' : ''}` }, text);
    if (full || !long) return [quote];
    const toggle = el('button', { type: 'button', class: 'bell-showall', onclick: (e) => { e.preventDefault(); e.stopPropagation(); const all = quote.classList.toggle('full'); toggle.textContent = all ? 'Show less' : 'Show all'; } }, 'Show all');
    return [quote, toggle];
  }

  /** The people's picture with the small icon of the kind. */
  function avatarWithIcon(n, kind) {
    const [icon, color] = KINDS[kind];
    const auto = ['automation', 'reminder'].includes(kind);
    let face;
    const who = n.actorName || whoOf(n);
    if (who && !auto && !/^@|\(GitHub\)/.test(who)) face = avatarEl({ name: who, avatar: n.actorAvatar || null }, 'tiny');
    else { face = el('span', { class: 'bell-face' }); face.innerHTML = SVG(icon).replace('width="11" height="11"', 'width="18" height="18"'); }
    const badgeNode = el('span', { class: 'bell-kind', style: `background:${color}`, title: KINDS[kind][2] });
    badgeNode.innerHTML = SVG(icon);
    const wrapNode = el('span', { class: `bell-avatar${['automation', 'reminder'].includes(kind) ? ' bell-avatar-auto' : ''}` }, face, badgeNode);
    if (['automation', 'reminder'].includes(kind)) wrapNode.style.setProperty('--k', color);
    return wrapNode;
  }

  const shownItems = () => items.filter((n) => {
    if (filter === 'mentions' && kindOf(n) !== 'mention') return false;
    if (typeFilter) {
      const kinds = TYPE_CHIPS.find((c) => c[0] === typeFilter)[2];
      if (!kinds.includes(kindOf(n))) return false;
    }
    if (boardFilter && n.board !== boardFilter) return false;
    return true;
  });

  /** The notifications as cards: by day, and the updates of one ticket on one day folded into its newest. */
  function listNodes(shown) {
    if (!shown.length) return [el('p', { class: 'muted bell-empty' }, query ? 'Nothing found.' : filter === 'unread' && !typeFilter && !boardFilter ? 'Nothing waiting: everything is read.' : filter === 'mentions' ? 'Nobody has mentioned you lately.' : filter === 'read' ? 'Nothing read yet.' : 'Nothing here.')];
    const out = [];
    let day = '';
    const groups = new Map(); // ticket of one day → its notifications, newest first
    for (const n of shown) {
      const key = `${dayKey(n.createdAt)}|${n.cardId}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(n);
    }
    for (const [key, group] of groups) {
      const head = group[0];
      if (dayKey(head.createdAt) !== day) { day = dayKey(head.createdAt); out.push(el('div', { class: 'bell-day' }, dayLabel(head.createdAt))); }
      out.push(cardNode(head, group, key));
    }
    return out;
  }

  function cardNode(head, group, key) {
    const kind = kindOf(head);
    const { key: ticket, name } = splitTitle(head.title);
    const unreadIn = group.filter((n) => !n.read);
    const hrefOf = (n) => `boards.html#${head.boardId}/${head.cardId}${n.meta?.commentId ? `/~${n.meta.commentId}` : ''}`; // (a comment's notification goes to the comment)
    const href = hrefOf(head);
    const closePanel = () => { panel.hidden = true; button.setAttribute('aria-expanded', 'false'); };
    /** One notification (or several) read or not read, without opening anything. */
    const setRead = async (list, value) => {
      for (const n of list) n.read = value;
      await read({ ids: list.map((n) => n.id), ...(value ? {} : { read: false }) });
      draw();
    };
    const sender = ['automation', 'reminder'].includes(kind) ? head.actorName || (kind === 'reminder' ? 'Reminder' : 'Automation') : head.board;
    const folded = group.slice(1);
    const foldedUnread = folded.filter((n) => !n.read).length;
    /** The ticket in a window of its own (a page of its own on a phone), and the notification stays as it is: not read. */
    const peek = (n) => el('a', { class: 'bell-act bell-peek', href: hrefOf(n), title: 'Open the ticket in a window without marking this notification as read', 'aria-label': 'Open the ticket without marking it as read',
      onclick: (e) => {
        e.stopPropagation();
        if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return; // (the browser's own new tab / window: it does not read it either)
        if (/KolvantageMobile\/|Android|iPhone|iPad/.test(navigator.userAgent)) return; // (a phone: the link opens the ticket here, the notification is not touched)
        e.preventDefault();
        const win = window.open(ticketWindowUrl(location.href, n.boardId, n.cardId, { solo: true }), '_blank', `popup=yes,width=${Math.min(1180, screen.availWidth - 80)},height=${Math.min(900, screen.availHeight - 80)}`);
        if (!win) location.href = hrefOf(n); // (pop-ups are blocked: it opens here, still not read)
      } }, '⧉ Open, keep unread');
    const act = (label, onclick) => el('button', { type: 'button', class: 'bell-act', onclick: (e) => { e.preventDefault(); e.stopPropagation(); onclick(); } }, label);
    // the newest one of the ticket: the card; opening it reads this one only
    const main = el('a', { class: `bell-main${head.read ? '' : ' unread'}`, href, onclick: () => { if (!head.read) { head.read = true; read({ ids: [head.id] }); } closePanel(); } },
      el('div', { class: 'bell-row1' }, ticket ? el('span', { class: 'bell-key' }, ticket) : null, el('span', { class: 'bell-board' }, sender || ''), el('span', { class: 'bell-time', title: new Date(head.createdAt).toLocaleString() }, ago(head.createdAt))),
      el('div', { class: 'bell-title', title: name }, name || head.title),
      whatNode(head, kind));
    const subs = folded.map((n) => el('div', { class: `bell-sub${n.read ? '' : ' unread'}` },
      el('div', { class: 'bell-sub-head' }, el('span', { class: 'bell-sub-dot', 'aria-hidden': 'true' }), el('span', { class: 'bell-sub-time', title: new Date(n.createdAt).toLocaleString() }, ago(n.createdAt)), el('span', { class: 'bell-sub-kind' }, KINDS[kindOf(n)][2])),
      el('a', { class: 'bell-sub-link', href: hrefOf(n), onclick: () => { if (!n.read) { n.read = true; read({ ids: [n.id] }); } closePanel(); } }, whatNode(n, kindOf(n), true)),
      el('div', { class: 'bell-sub-actions' }, peek(n), act(n.read ? 'Mark as not read' : 'Mark as read', () => setRead([n], !n.read)))));
    return el('div', { class: `bell-card${unreadIn.length ? ' unread' : ''}` },
      avatarWithIcon(head, kind),
      el('div', { class: 'bell-body' },
        main,
        folded.length ? el('button', { type: 'button', class: 'bell-more', 'aria-expanded': String(open.has(key)), onclick: (e) => { e.preventDefault(); e.stopPropagation(); if (open.has(key)) open.delete(key); else open.add(key); draw(); } }, `${open.has(key) ? '−' : '＋'} ${folded.length} more update${folded.length === 1 ? '' : 's'} on this ticket${foldedUnread ? ` (${foldedUnread} not read)` : ''} ${open.has(key) ? '▴' : '▾'}`) : null,
        folded.length && open.has(key) ? el('div', { class: 'bell-folded' }, ...subs) : null,
        el('div', { class: 'bell-actions' },
          peek(head), // (the card itself opens the ticket and reads it: no second "Open")
          act(head.read ? 'Mark as not read' : 'Mark as read', () => setRead([head], !head.read)),
          unreadIn.length > 1 || (folded.length && unreadIn.some((n) => n !== head) && !head.read) ? act(`Mark all ${group.length} as read`, () => setRead(group, true)) : null)));
  }

  const draw = () => {
    paintBadge();
    if (panel.hidden) return;
    const shown = shownItems();
    const tabs = el('div', { class: 'bell-tabs', role: 'tablist', 'aria-label': 'Show' }, ...[['unread', 'Not read'], ['all', 'All'], ['mentions', 'Mentions'], ['read', 'Read']].map(([key, label]) => el('button', {
      type: 'button', role: 'tab', class: `bell-tab${filter === key ? ' on' : ''}`, 'aria-selected': String(filter === key), onclick: (e) => { e.stopPropagation(); filter = key; draw(); refresh(); },
    }, label, key === 'unread' && unread ? el('i', { class: 'bell-count' }, String(unread > 99 ? '99+' : unread)) : null)));
    const boards = [...new Set(items.map((n) => n.board).filter(Boolean))];
    const chips = el('div', { class: 'bell-chips' },
      boards.length > 1 ? Object.assign(el('select', { class: `bell-board-select${boardFilter ? ' on' : ''}`, 'aria-label': 'Board', onchange: (e) => { boardFilter = e.target.value; draw(); } }, el('option', { value: '' }, 'All boards'), ...boards.map((b) => el('option', { value: b }, b))), { value: boardFilter }) : null,
      ...TYPE_CHIPS.map(([key, label]) => el('button', { type: 'button', class: `bell-chip${typeFilter === key ? ' on' : ''}`, 'aria-pressed': String(typeFilter === key), onclick: (e) => { e.stopPropagation(); typeFilter = typeFilter === key ? '' : key; draw(); } }, label)));
    const pushRow = el('div', { class: 'bell-push', hidden: !pushOpen });
    const typing = document.activeElement === search;
    const kept = panel.querySelector('.bell-list')?.scrollTop || 0; // (drawing again must not send the person back to the top of the list)
    const gear = el('button', { type: 'button', class: 'bell-gear', title: 'Push notifications on this device', 'aria-label': 'Push notification settings', 'aria-expanded': String(pushOpen), onclick: (e) => { e.stopPropagation(); pushOpen = !pushOpen; draw(); } }, '⚙');
    panel.replaceChildren(
      el('div', { class: 'bell-head' }, el('strong', {}, 'Notifications'), el('span', { class: 'spacer' }), el('button', { type: 'button', class: 'btn-small', disabled: unread === 0, onclick: async () => { items.forEach((n) => { n.read = true; }); await read({ all: true }); draw(); } }, '✓ Mark all as read'), gear),
      tabs,
      search,
      chips,
      el('div', { class: 'bell-list' }, ...listNodes(shown)),
      pushRow);
    const listNode = panel.querySelector('.bell-list');
    if (listNode && kept) listNode.scrollTop = kept;
    if (typing) search.focus();
    pushControl(pushRow);
  };
  let pushOpen = false; // the push settings (turn on or off, a test) are behind the gear

  /** The switch for push notifications on this device. */
  async function pushControl(row) {
    if (inDesktopApp()) return row.replaceChildren(el('span', { class: 'small' }, '🔔 In this app, updates to your tickets show as notifications of your computer while the app is running (even when its window is hidden). They follow the alert settings in My account.'));
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return row.replaceChildren(el('span', { class: 'muted small' }, 'Push notifications are not available in this browser.'));
    if (Notification.permission === 'denied') return row.replaceChildren(el('span', { class: 'muted small' }, 'Push notifications are blocked for this site in your browser’s settings.'));
    let reg = await navigator.serviceWorker.getRegistration('/dashboard/').catch(() => null);
    const sub = reg ? await reg.pushManager.getSubscription().catch(() => null) : null;
    const toggle = el('button', { type: 'button', class: 'btn-small' }, sub ? 'Turn off' : 'Turn on');
    const test = sub ? el('button', { type: 'button', class: 'btn-small', title: 'Send a test notification to your devices and see what the push service answers' }, 'Send a test') : null;
    row.replaceChildren(el('span', { class: 'small' }, sub ? '🔔 Push notifications are on for this device' : 'Get push notifications on this device, even when Kolvantage is closed'), toggle, ...(test ? [test] : []));
    /** Sends a test to this person's devices and says in words what happened. */
    const sendTest = async () => {
      const r = await api('/board-notifications/push/test', { method: 'POST' });
      if (!r.devices) return toast('No device is turned on yet', 'bad');
      const bad = r.results.filter((x) => !x.ok);
      if (!bad.length) return toast(`Test sent to ${r.devices} device${r.devices === 1 ? '' : 's'}: it should show in a moment (if it does not, see your computer's notification settings)`);
      toast(`The ${bad.map((x) => x.service).join(', ')} push service did not take it: ${bad[0].error}${bad[0].gone ? ' (this device was dropped: turn it on again)' : ''}`, 'bad');
    };
    test?.addEventListener('click', async () => { test.disabled = true; try { await sendTest(); } catch (err) { toast(err.message, 'bad'); } test.disabled = false; });
    toggle.addEventListener('click', async () => {
      toggle.disabled = true;
      try {
        if (sub) {
          await api('/board-notifications/push', { method: 'DELETE', body: { endpoint: sub.endpoint } });
          await sub.unsubscribe();
        } else {
          // The browser's question comes FIRST, straight from the click: after the waits below the browser no longer sees a click behind it, and may not let the
          // person answer ("Allow" that cannot be pressed) or may take the question for a refusal.
          let permission = Notification.permission;
          if (permission === 'default') permission = await Notification.requestPermission();
          if (permission === 'denied') throw new Error('Your browser blocks notifications for this site. Click the lock (or the settings icon) next to the address, set Notifications to Allow, then turn this on again.');
          if (permission !== 'granted') throw new Error('The question was closed without an answer. Click Turn on again and choose Allow. If no question appears, click the lock or bell icon next to the address and set Notifications to Allow.');
          reg = await navigator.serviceWorker.register('/dashboard/sw.js', { scope: '/dashboard/' });
          await navigator.serviceWorker.ready;
          const { publicKey } = await api('/board-notifications/push');
          let made;
          try { made = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlToBytes(publicKey) }); } catch (err) {
            throw new Error(`The browser could not connect to its push service (${err.name}). In Brave, turn on “Use Google services for push messaging” in Settings → Privacy and security; in other browsers check that notifications and background sync are not blocked.`);
          }
          await api('/board-notifications/push', { method: 'POST', body: { subscription: made.toJSON() } });
          toast('Push notifications are on for this device');
          await sendTest().catch(() => {}); // (a test straight away, so a device that cannot receive them says so now)
        }
      } catch (err) {
        toast(err.message, 'bad');
      }
      pushControl(row);
    });
  }

  const refresh = async () => {
    try {
      const params = new URLSearchParams({ limit: '100', ...(filter === 'unread' || filter === 'read' ? { filter } : {}), ...(query ? { q: query } : {}) }).toString();
      const r = await api(`/board-notifications${params ? `?${params}` : ''}`);
      wrap.hidden = !r.enabled;
      items = r.items;
      unread = r.unread;
      draw();
    } catch { /* offline: the next time */ }
  };

  button.addEventListener('click', () => {
    panel.hidden = !panel.hidden;
    button.setAttribute('aria-expanded', String(!panel.hidden));
    if (!panel.hidden) { draw(); refresh(); }
  });
  document.addEventListener('click', (e) => { if (!panel.hidden && !wrap.contains(e.target)) { panel.hidden = true; button.setAttribute('aria-expanded', 'false'); } });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !panel.hidden) { panel.hidden = true; button.setAttribute('aria-expanded', 'false'); button.focus(); } });
  // a new one, from the Inbox stream
  addEventListener('kv-boardnotice', (e) => {
    const n = e.detail;
    wrap.hidden = false;
    if (filter !== 'read') items = [{ ...n, read: false }, ...items.filter((x) => x.id !== n.id)].slice(0, 40);
    unread = n.unread ?? unread + 1;
    draw();
    toast(`${n.title}: ${n.summary}`);
    // in the Desktop app: a notification of the computer when the window is hidden or not in front
    const away = document.visibilityState !== 'visible' || !document.hasFocus();
    if (inDesktopApp() && away && desktopAlertsOn()) {
      try {
        const note = new Notification(n.title, { body: String(n.summary || '').slice(0, 140), tag: `board-${n.id}` });
        note.onclick = () => {
          window.focus();
          if (n.boardId && n.cardId) location.href = `boards.html#${n.boardId}/${n.cardId}${n.meta?.commentId ? `/~${n.meta.commentId}` : ''}`;
          note.close();
        };
      } catch { /* the computer would not show it */ }
    }
  });
  setInterval(() => { if (!document.hidden) refresh(); }, 60000);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refresh(); });
  refresh();
  return wrap;
}
